// Setup › Templates (`/c/{slug}/setup/templates`, M1-06; core, managers): the company's
// templates, and for the open one (`?template=`) its parameters, a preview of what applying it
// would do (`POST …/preview`, which writes nothing) grouped by kind with create / reuse / link
// / skip and the reason for each, and Apply behind a confirmation that repeats the counts.
// Spaces, channels, boards and starter tasks are created through the chat and boards modules;
// an entry the API skips (a metric key the company lacks, or a work module that is not
// registered) is listed with its reason rather than pretended. Applying refreshes seats, roles,
// teams and, when it made any, Work's and Chat's spaces and boards.
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Input,
  Label,
  Notice,
  SettingsGroup,
  SettingsRow,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LayoutTemplateIcon, PencilIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { type ComponentProps, type FormEvent, useState } from "react";
import type { Api } from "../../api/client";
import type { Template, TemplateApplied, TemplatePlanItem, TemplatePreview } from "../../api/types";
import type { CompanyAccess } from "../company";
import { ConfirmRowAction } from "../confirm-row-action";
import { keys, orgKeys, structureKeys, templateQuery, templatesQuery } from "../queries";
import { Loading } from "../screen-parts";
import { DENSE_TABLE, RowAction, RowActions } from "../settings/row-actions";
import { SetupPage } from "../settings/setup-page";
import { ConfirmButton, ErrorNotice, FormError, MoreRows, problemCode } from "./parts";

type Kind = TemplatePlanItem["kind"];
type Action = TemplatePlanItem["action"];

export const KIND_LABELS: Record<Kind, string> = {
  role: "Roles",
  team: "Teams",
  team_member: "Team members",
  seat: "Seats",
  responsibility: "Responsibilities",
  recurring_work: "Recurring work",
  metric: "Metrics",
  space: "Spaces",
  channel: "Channels",
  board: "Boards",
  field: "Fields",
  view: "Views",
  task: "Tasks",
};
const KINDS = Object.keys(KIND_LABELS) as Kind[];

const ACTION_LABELS: Record<Action, string> = {
  create: "Create",
  reuse: "Reuse",
  link: "Link",
  skip: "Skip",
};
const ACTION_TONE: Record<Action, NonNullable<ComponentProps<typeof Badge>["variant"]>> = {
  create: "success",
  reuse: "info",
  link: "accent",
  skip: "neutral",
};
const ACTION_SUMMARY: Record<Action, string> = {
  create: "to create",
  reuse: "reused",
  link: "linked",
  skip: "skipped",
};

const rowKey = (i: TemplatePlanItem) => `${i.kind}|${i.parent ?? ""}|${i.key ?? ""}|${i.name}`;

/** "4 to create · 2 reused · 1 linked · 6 skipped". */
export function planSummary(items: TemplatePlanItem[]): string {
  const counts = (["create", "reuse", "link", "skip"] as Action[])
    .map((a) => [a, items.filter((i) => i.action === a).length] as const)
    .filter(([, n]) => n > 0);
  return counts.length
    ? counts.map(([a, n]) => `${n} ${ACTION_SUMMARY[a]}`).join(" · ")
    : "Nothing to do";
}

export function TemplatesScreen({
  api,
  company,
  templateId,
  onTemplate,
  onOpenSeats,
}: {
  api: Api;
  company: CompanyAccess;
  /** The open template (`?template=`); the first one when absent. */
  templateId: string | undefined;
  onTemplate: (id: string | undefined) => void;
  onOpenSeats: () => void;
}) {
  const list = useInfiniteQuery(templatesQuery(api, company.id));
  const all = list.data?.pages.flatMap((p) => p.data) ?? [];
  const listed = templateId ? all.find((t) => t.id === templateId) : all[0];
  // A link to a template beyond the loaded pages reads that one template.
  const single = useQuery({
    ...templateQuery(api, company.id, templateId ?? ""),
    enabled: !!templateId && !!list.data && !listed,
  });
  const current = listed ?? single.data;
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Template | "new" | null>(null);
  const remove = useMutation({
    mutationFn: (t: Template) =>
      api.client.templates.delete({ params: { co: company.id, id: t.id } }),
    onSuccess: (_, t) => {
      void qc.invalidateQueries({ queryKey: orgKeys.templates(company.id) });
      if (t.id === current?.id) onTemplate(undefined);
    },
  });
  return (
    <SetupPage
      title="Templates"
      {...(list.data ? { count: all.length } : {})}
      actions={
        <Button size="sm" onClick={() => setEditing("new")} disabled={!list.data}>
          <PlusIcon aria-hidden={true} />
          New template
        </Button>
      }
    >
      <FormError what="Deleting the template" error={remove.error} />
      {list.isPending ? (
        <Loading label="Loading templates" rows={4} />
      ) : list.isError ? (
        <ErrorNotice what="Templates" error={list.error} />
      ) : !all.length ? (
        <EmptyState
          variant="card"
          icon={LayoutTemplateIcon}
          title="No templates"
          description="A template lists the roles, teams, seats and starter work to set up in one go."
          actions={<Button onClick={() => setEditing("new")}>New template</Button>}
        />
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,280px)_minmax(0,1fr)]">
          <div className="flex min-w-0 flex-col">
            <Table data-slot="templates" className={DENSE_TABLE}>
              <TableCaption className="sr-only">Templates of {company.name}</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead scope="col">Template</TableHead>
                  <TableHead scope="col" className="text-right">
                    Version
                  </TableHead>
                  <TableHead scope="col">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {all.map((t) => (
                  <TableRow
                    key={t.id}
                    className="group/row"
                    data-template={t.id}
                    data-state={t.id === current?.id ? "selected" : undefined}
                  >
                    <TableHead scope="row">
                      <button
                        type="button"
                        aria-current={t.id === current?.id ? "true" : undefined}
                        className="text-left font-medium text-foreground underline-offset-4 hover:underline"
                        onClick={() => onTemplate(t.id)}
                      >
                        {t.name}
                      </button>
                      {t.description ? (
                        <span className="block truncate text-xs font-normal text-muted-foreground">
                          {t.description}
                        </span>
                      ) : null}
                    </TableHead>
                    <TableCell className="text-right tabular-nums">v{t.version}</TableCell>
                    <TableCell className="w-16">
                      <RowActions>
                        <RowAction
                          icon={PencilIcon}
                          label={`Edit ${t.name}`}
                          tip="Edit"
                          onClick={() => setEditing(t)}
                        />
                        <ConfirmRowAction
                          icon={Trash2Icon}
                          label={`Delete ${t.name}`}
                          tip="Delete"
                          disabled={remove.isPending}
                          title={`Delete "${t.name}"?`}
                          description="The template is removed. Whatever it already created (seats, roles, responsibilities) stays."
                          confirmLabel="Delete template"
                          onConfirm={() => remove.mutate(t)}
                        />
                      </RowActions>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <MoreRows what="templates" q={list} />
          </div>
          {current ? (
            <TemplatePanel
              key={`${current.id}@${current.version}`}
              api={api}
              company={company}
              template={current}
              onDeleted={() => onTemplate(undefined)}
              onOpenSeats={onOpenSeats}
            />
          ) : single.isPending && templateId ? (
            <Loading label="Loading the template" rows={3} />
          ) : (
            <ErrorNotice
              what="The template"
              error={single.error ?? new Error("No such template.")}
            />
          )}
        </div>
      )}
      {editing ? (
        <TemplateDialog
          api={api}
          company={company}
          template={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(t) => {
            setEditing(null);
            onTemplate(t.id);
          }}
        />
      ) : null}
    </SetupPage>
  );
}

/** The spec a new template starts from: version 1, nothing in it yet. */
const BLANK_SPEC = '{\n  "version": 1\n}';

/**
 * Create a template, or edit one's name, description and spec (`POST` / `PATCH /templates`). The
 * spec is the JSON the API checks; a changed spec bumps the version, and the API's problems
 * show under the form.
 */
function TemplateDialog({
  api,
  company,
  template: t,
  onClose,
  onSaved,
}: {
  api: Api;
  company: CompanyAccess;
  template: Template | null;
  onClose: () => void;
  onSaved: (t: Template) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(t?.name ?? "");
  const [description, setDescription] = useState(t?.description ?? "");
  const [specText, setSpecText] = useState(t ? JSON.stringify(t.spec, null, 2) : BLANK_SPEC);
  let spec: unknown = null;
  let specError: string | null = null;
  try {
    spec = JSON.parse(specText);
  } catch {
    specError = "The spec is not valid JSON.";
  }
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        description: description.trim() || null,
        spec: spec as Template["spec"],
      };
      return t
        ? api.client.templates.update({ params: { co: company.id, id: t.id }, body })
        : api.client.templates.create({ params: { co: company.id }, body });
    },
    onSuccess: (saved) => {
      void qc.invalidateQueries({ queryKey: orgKeys.templates(company.id) });
      onSaved(saved);
    },
  });
  const ok = name.trim().length > 0 && !specError;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ok && !save.isPending) save.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-xl">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{t ? `Edit "${t.name}"` : "New template"}</DialogTitle>
            <DialogDescription>
              {t
                ? "Changing the spec makes a new version. What it already created stays."
                : "Start from an empty spec, or paste one."}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="template-name">Name</Label>
            <Input
              id="template-name"
              value={name}
              maxLength={200}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="template-description">Description</Label>
            <Input
              id="template-description"
              value={description}
              maxLength={2000}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="template-spec">Spec (JSON)</Label>
            <Textarea
              id="template-spec"
              rows={12}
              className="font-mono text-xs"
              value={specText}
              aria-invalid={specError ? true : undefined}
              onChange={(e) => setSpecText(e.target.value)}
            />
            {specError ? <p className="text-xs text-danger-text">{specError}</p> : null}
          </div>
          <FormError
            what={t ? "Saving the template" : "Creating the template"}
            error={save.error}
          />
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!ok || save.isPending}>
              {save.isPending ? "Saving…" : t ? "Save" : "Create template"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TemplatePanel({
  api,
  company,
  template: t,
  onDeleted,
  onOpenSeats,
}: {
  api: Api;
  company: CompanyAccess;
  template: Template;
  onDeleted: () => void;
  onOpenSeats: () => void;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const declared = t.spec.params ?? [];
  const [values, setValues] = useState<Record<string, string>>({});
  const [previewedWith, setPreviewedWith] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [applied, setApplied] = useState<{
    result: TemplateApplied;
    items: TemplatePlanItem[];
  } | null>(null);
  const params = Object.fromEntries(
    Object.entries(values)
      .map(([k, v]) => [k, v.trim()] as const)
      .filter(([, v]) => v !== ""),
  );
  const paramsKey = JSON.stringify(params);
  const preview = useMutation({
    mutationFn: (body: { params: Record<string, string> }) =>
      api.client.templates.preview({ params: { co, id: t.id }, body }),
    onSuccess: () => setPreviewedWith(paramsKey),
  });
  const apply = useMutation({
    mutationFn: () => api.client.templates.apply({ params: { co, id: t.id }, body: { params } }),
    onSuccess: (result) => {
      setApplied({ result, items: preview.data?.items ?? [] });
      setConfirming(false);
      preview.reset();
      setPreviewedWith(null);
      for (const key of [
        keys.seats(co),
        structureKeys.roles(co),
        orgKeys.teams(co),
        orgKeys.orgChart(co),
        orgKeys.templates(co),
        ["company", co, "responsibilities"],
        orgKeys.allRecurringWork(co),
      ])
        void qc.invalidateQueries({ queryKey: key });
      // The spaces, channels, boards and tasks it made: Work's and Chat's lists (not Work's
      // live probe).
      if (result.created.spaces.length || result.created.boards.length) {
        void qc.invalidateQueries({
          queryKey: ["company", co, "work"],
          predicate: (q) => q.queryKey[3] !== "live",
        });
        void qc.invalidateQueries({ queryKey: ["company", co, "chat"] });
      }
    },
  });
  const remove = useMutation({
    mutationFn: () => api.client.templates.delete({ params: { co, id: t.id } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgKeys.templates(co) });
      onDeleted();
    },
  });
  const stale = !!preview.data && previewedWith !== paramsKey;
  const canApply = !!preview.data?.valid && !stale && !apply.isPending;
  const onPreview = (e: FormEvent) => {
    e.preventDefault();
    setApplied(null);
    preview.mutate({ params });
  };
  return (
    <section
      className="flex min-w-0 flex-col gap-4"
      aria-labelledby="template-title"
      data-slot="template-panel"
    >
      <form
        onSubmit={onPreview}
        className="flex min-w-0 flex-col gap-3"
        aria-label="Template parameters"
      >
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-0 flex-1">
            <h2 id="template-title" className="truncate text-[15px] font-semibold text-foreground">
              {t.name}
            </h2>
            <p className="truncate text-xs text-muted-foreground">
              Version {t.version}
              {t.description ? ` · ${t.description}` : ""}
            </p>
          </div>
          <Button type="submit" size="sm" variant="secondary" disabled={preview.isPending}>
            {preview.isPending ? "Previewing…" : "Preview"}
          </Button>
          <Button type="button" size="sm" disabled={!canApply} onClick={() => setConfirming(true)}>
            Apply…
          </Button>
          <ConfirmButton
            label="Delete template"
            variant="ghost"
            title={`Delete "${t.name}"?`}
            description="The template is removed. Whatever it already created (seats, roles, responsibilities) stays."
            confirmLabel="Delete template"
            pending={remove.isPending}
            onConfirm={() => remove.mutateAsync()}
          />
        </div>
        <p className="text-xs text-muted-foreground" data-slot="template-status">
          {!preview.data
            ? "Preview first: applying is enabled once the preview has no problems."
            : stale
              ? "The parameters changed: preview again before applying."
              : preview.data.valid
                ? planSummary(preview.data.items)
                : "Fix the problems below, then preview again."}
        </p>
        {declared.length ? (
          <SettingsGroup title="Parameters" count={declared.length}>
            {declared.map((p) => (
              <SettingsRow
                key={p.key}
                htmlFor={`template-param-${p.key}`}
                label={
                  <>
                    {p.label}
                    {p.required ? <span className="text-muted-foreground"> (required)</span> : null}
                  </>
                }
                {...(p.default ? { hint: `Default: ${p.default}` } : {})}
              >
                <Input
                  id={`template-param-${p.key}`}
                  className="w-64 max-md:w-full"
                  value={values[p.key] ?? ""}
                  placeholder={p.default ?? ""}
                  maxLength={200}
                  aria-required={p.required || undefined}
                  onChange={(e) => setValues((v) => ({ ...v, [p.key]: e.target.value }))}
                />
              </SettingsRow>
            ))}
          </SettingsGroup>
        ) : null}
      </form>
      <FormError what="Deleting the template" error={remove.error} />
      <FormError what="Previewing" error={preview.error} />

      {applied ? <AppliedView applied={applied} onOpenSeats={onOpenSeats} /> : null}
      {preview.data ? <PlanView preview={preview.data} /> : null}

      {confirming && preview.data ? (
        <Dialog open onOpenChange={(open) => !open && setConfirming(false)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Apply "{t.name}"?</DialogTitle>
              <DialogDescription>
                Everything below is created in one step. It cannot be undone in one step: remove
                what you do not want afterwards.
              </DialogDescription>
            </DialogHeader>
            <ApplySummary items={preview.data.items} />
            {problemCode(apply.error) === "template_invalid" ? (
              <Notice tone="danger" role="alert" title="The template no longer applies">
                Something changed since the preview. Preview again.
              </Notice>
            ) : (
              <FormError what="Applying the template" error={apply.error} />
            )}
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
              <Button type="button" disabled={apply.isPending} onClick={() => apply.mutate()}>
                {apply.isPending ? "Applying…" : "Apply template"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </section>
  );
}

/** The confirmation's summary: per action, the count per kind. */
function ApplySummary({ items }: { items: TemplatePlanItem[] }) {
  return (
    <dl className="flex flex-col gap-2 text-sm" data-slot="apply-summary">
      {(["create", "reuse", "link", "skip"] as Action[]).map((a) => {
        const of = items.filter((i) => i.action === a);
        if (!of.length) return null;
        const per = KINDS.map((k) => [k, of.filter((i) => i.kind === k).length] as const)
          .filter(([, n]) => n > 0)
          .map(([k, n]) => `${n} ${KIND_LABELS[k].toLowerCase()}`)
          .join(", ");
        return (
          <div key={a} className="flex flex-wrap items-baseline gap-2" data-action={a}>
            <dt>
              <Badge variant={ACTION_TONE[a]}>{ACTION_LABELS[a]}</Badge>
            </dt>
            <dd className="min-w-0 flex-1 text-secondary">
              {per}
              {a === "skip" ? (
                <span className="text-muted-foreground"> (missing or not available)</span>
              ) : null}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

/** The preview: problems first, then one table per kind. */
function PlanView({ preview }: { preview: TemplatePreview }) {
  return (
    <div className="flex flex-col gap-5" data-slot="template-plan">
      {preview.problems.length ? (
        <Notice
          tone="danger"
          role="alert"
          title="This template cannot be applied yet"
          data-slot="template-problems"
        >
          <ul className="list-disc pl-5">
            {preview.problems.map((p) => (
              <li key={`${p.path}|${p.message}`}>
                <code className="font-mono text-xs">{p.path}</code>: {p.message}
              </li>
            ))}
          </ul>
        </Notice>
      ) : null}
      {KINDS.map((kind) => {
        const items = preview.items.filter((i) => i.kind === kind);
        if (!items.length) return null;
        return (
          <Table key={kind} data-kind={kind} className={DENSE_TABLE}>
            <TableCaption className="text-left font-medium text-foreground">
              {KIND_LABELS[kind]} ({items.length})
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Name</TableHead>
                <TableHead scope="col">Under</TableHead>
                <TableHead scope="col">Action</TableHead>
                <TableHead scope="col">Note</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((i) => (
                <TableRow key={rowKey(i)} data-action={i.action}>
                  <TableHead scope="row">{i.name}</TableHead>
                  <TableCell className="text-muted-foreground">{i.parent ?? "—"}</TableCell>
                  <TableCell>
                    <Badge variant={ACTION_TONE[i.action]}>{ACTION_LABELS[i.action]}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {i.reason ?? (i.action === "create" ? "New" : "")}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );
      })}
    </div>
  );
}

const CREATED_LABELS: [keyof TemplateApplied["created"], string, string][] = [
  ["roles", "role", "roles"],
  ["teams", "team", "teams"],
  ["seats", "seat", "seats"],
  ["responsibilities", "responsibility", "responsibilities"],
  ["recurring_work", "recurring work", "recurring work"],
  ["spaces", "space", "spaces"],
  ["channels", "channel", "channels"],
  ["boards", "board", "boards"],
  ["tasks", "task", "tasks"],
];

/** What the application created (from its ids) and what it skipped, with the reasons. */
function AppliedView({
  applied,
  onOpenSeats,
}: {
  applied: { result: TemplateApplied; items: TemplatePlanItem[] };
  onOpenSeats: () => void;
}) {
  const { result, items } = applied;
  const made = CREATED_LABELS.map(
    ([k, one, many]) => [result.created[k].length, one, many] as const,
  )
    .filter(([n]) => n > 0)
    .map(([n, one, many]) => `${n} ${n === 1 ? one : many}`);
  const seats = items.filter((i) => i.kind === "seat" && i.action === "create").map((i) => i.name);
  return (
    <div className="flex flex-col gap-3" data-slot="template-applied">
      <Notice
        tone="success"
        role="status"
        title={`Applied version ${result.version}`}
        action={
          result.created.seats.length ? (
            <Button size="sm" variant="secondary" onClick={onOpenSeats}>
              Open Setup › Seats
            </Button>
          ) : null
        }
      >
        Created {made.length ? made.join(", ") : "nothing new"}.
        {seats.length ? ` New seats: ${seats.join(", ")}.` : ""}
      </Notice>
      {result.skipped.length ? (
        <Table data-slot="template-skipped" className={DENSE_TABLE}>
          <TableCaption className="text-left">
            Skipped ({result.skipped.length}): nothing was created for these.
          </TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">Name</TableHead>
              <TableHead scope="col">Kind</TableHead>
              <TableHead scope="col">Why</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {result.skipped.map((i) => (
              <TableRow key={rowKey(i)}>
                <TableHead scope="row">{i.name}</TableHead>
                <TableCell>{KIND_LABELS[i.kind]}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{i.reason ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}
    </div>
  );
}
